import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Image,
  Divider,
  Wrap,
  WrapItem,
  Collapse,
  Code,
  IconButton,
  Tooltip,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  CloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Textarea,
  Link,
  Flex,
} from '@chakra-ui/react';
import {
  FaCloud,
  FaSignOutAlt,
  FaBookOpen,
  FaChevronRight,
  FaCode,
  FaCopy,
  FaRocket,
  FaArrowLeft,
  FaFileAlt,
  FaCubes,
  FaFlask,
  FaWater,
  FaBomb,
  FaWind,
  FaFire,
  FaFolder,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaBolt,
  FaMagnet,
  FaHammer,
  FaAtom,
  FaSlidersH,
  FaTachometerAlt,
  FaCheckCircle,
  FaPlay,
  FaEdit,
  FaCompressArrowsAlt,
  FaTint,
  FaWaveSquare,
  FaCrosshairs,
  FaBriefcase,
  FaCircleNotch
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import JSZip from 'jszip';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import examplesApiService from '../services/examplesApi';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const DEFAULT_CATEGORY_META = { icon: FaFolder, color: 'gray', gradient: 'linear-gradient(135deg, #4A5568, #718096)' };

const CATEGORY_META = {
  // Current API taxonomy (pyfr/templates)
  external_aerodynamics: { icon: FaWind, color: 'cyan', gradient: 'linear-gradient(135deg, #0891b2, #155e75)' },
  high_speed_compressible: { icon: FaBolt, color: 'red', gradient: 'linear-gradient(135deg, #C53030, #E53E3E)' },
  turbulence_les: { icon: FaFire, color: 'pink', gradient: 'linear-gradient(135deg, #db2777, #9d174d)' },
  viscous_wall_bounded: { icon: FaWater, color: 'teal', gradient: 'linear-gradient(135deg, #2C7A7B, #38B2AC)' },
  incompressible_low_mach: { icon: FaCompressArrowsAlt, color: 'blue', gradient: 'linear-gradient(135deg, #2A4365, #63B3ED)' },
  vortex_dynamics: { icon: FaCrosshairs, color: 'blue', gradient: 'linear-gradient(135deg, #2A4365, #63B3ED)' },
  verification_benchmark: { icon: FaCheckCircle, color: 'green', gradient: 'linear-gradient(135deg, #22543D, #68D391)' },
  // Legacy fallbacks
  compressible_multiphase: { icon: FaCubes, color: 'purple', gradient: 'linear-gradient(135deg, #6B46C1, #805AD5)' },
  biomedical_bubble: { icon: FaFlask, color: 'pink', gradient: 'linear-gradient(135deg, #B83280, #D53F8C)' },
  aerospace_shock: { icon: FaWind, color: 'cyan', gradient: 'linear-gradient(135deg, #0891b2, #155e75)' },
  underwater_explosion: { icon: FaBomb, color: 'orange', gradient: 'linear-gradient(135deg, #C05621, #DD6B20)' },
  high_speed_jet: { icon: FaFire, color: 'red', gradient: 'linear-gradient(135deg, #C53030, #E53E3E)' },
  cavitation: { icon: FaWater, color: 'teal', gradient: 'linear-gradient(135deg, #2C7A7B, #38B2AC)' },
  acoustic_wave: { icon: FaWaveSquare, color: 'cyan', gradient: 'linear-gradient(135deg, #0987A0, #00B5D8)' },
  classical_shock_tube: { icon: FaBolt, color: 'yellow', gradient: 'linear-gradient(135deg, #B7791F, #D69E2E)' },
  numerical_schemes: { icon: FaSlidersH, color: 'gray', gradient: 'linear-gradient(135deg, #4A5568, #718096)' },
  magnetohydrodynamics: { icon: FaMagnet, color: 'purple', gradient: 'linear-gradient(135deg, #553C9A, #9F7AEA)' },
  solid_mechanics: { icon: FaHammer, color: 'orange', gradient: 'linear-gradient(135deg, #9C4221, #ED8936)' },
  reactive_multispecies: { icon: FaAtom, color: 'green', gradient: 'linear-gradient(135deg, #276749, #48BB78)' },
  hydrodynamic_instability: { icon: FaCompressArrowsAlt, color: 'red', gradient: 'linear-gradient(135deg, #9B2C2C, #FC8181)' },
  immersed_boundary: { icon: FaCubes, color: 'teal', gradient: 'linear-gradient(135deg, #234E52, #4FD1C5)' },
  shock_droplet: { icon: FaTint, color: 'blue', gradient: 'linear-gradient(135deg, #2C5282, #90CDF4)' },
  viscous_flow: { icon: FaWater, color: 'purple', gradient: 'linear-gradient(135deg, #44337A, #B794F4)' },
  performance_scaling: { icon: FaTachometerAlt, color: 'red', gradient: 'linear-gradient(135deg, #742A2A, #F56565)' },
  tire_industry: { icon: FaCircleNotch, color: 'gray', gradient: 'linear-gradient(135deg, #4A5568, #718096)' },
};

const getCategoryMeta = (tag) => CATEGORY_META[tag] || DEFAULT_CATEGORY_META;

/** Convert API LaTeX delimiters (\( \), \[ \]) to remark-math format.
 *  Display math \[ \] is emitted as block math on its own lines so remark-math
 *  treats it as flow math; inline \( \) becomes $ $. */
const prepareMarkdownMath = (text) => {
  if (!text) return text;
  let s = text;
  s = s.replace(/\\\[([\s\S]*?)\\\]/g, (_, math) => `\n\n$$\n${math.trim()}\n$$\n\n`);
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, (_, math) => `$${math.trim()}$`);
  return s;
};

const getBusinessCaseUrl = (exampleName) => `/business-case/${encodeURIComponent(exampleName)}`;

const getDimensionColor = (dim) => {
  switch (dim) {
    case '0D': return 'gray';
    case '1D': return 'green';
    case '2D': return 'blue';
    case '3D': return 'purple';
    default: return 'gray';
  }
};

const CategoryCard = ({ category, onClick }) => {
  const meta = getCategoryMeta(category.tag);
  const cardBg = useColorModeValue('white', 'gray.700');
  const dimensions = [...new Set(category.examples.map(e => e.dimension))].sort();

  return (
    <Card
      bg={cardBg}
      shadow="md"
      cursor="pointer"
      onClick={onClick}
      transition="all 0.2s"
      _hover={{ shadow: 'xl', transform: 'translateY(-4px)' }}
      overflow="hidden"
    >
      <Box bg={meta.gradient} px={6} py={5}>
        <HStack spacing={3}>
          <Icon as={meta.icon} boxSize={8} color="white" />
          <VStack align="start" spacing={0}>
            <Heading size="md" color="white">{category.label}</Heading>
            <Text fontSize="sm" color="whiteAlpha.800">
              {category.count} {category.count === 1 ? 'example' : 'examples'}
            </Text>
          </VStack>
        </HStack>
      </Box>
      <CardBody>
        <Text fontSize="sm" color="gray.600" mb={3}>
          {category.description}
        </Text>
        {category.domains && category.domains.length > 0 && (
          <Wrap spacing={1} mb={2}>
            {category.domains.map(domain => (
              <WrapItem key={domain}>
                <Badge colorScheme="gray" variant="outline" fontSize="2xs" fontWeight="medium">{domain}</Badge>
              </WrapItem>
            ))}
          </Wrap>
        )}
        {category.keywords && category.keywords.length > 0 && (
          <Wrap spacing={1} mb={3}>
            {category.keywords.slice(0, 6).map(kw => (
              <WrapItem key={kw}>
                <Badge colorScheme="brand" variant="subtle" fontSize="2xs">{kw}</Badge>
              </WrapItem>
            ))}
            {category.keywords.length > 6 && (
              <WrapItem>
                <Badge colorScheme="gray" variant="subtle" fontSize="2xs">+{category.keywords.length - 6}</Badge>
              </WrapItem>
            )}
          </Wrap>
        )}
        <HStack justify="space-between" align="center">
          <Wrap spacing={2}>
            {dimensions.map(dim => (
              <WrapItem key={dim}>
                <Badge colorScheme={getDimensionColor(dim)} variant="subtle">{dim}</Badge>
              </WrapItem>
            ))}
          </Wrap>
          <Badge colorScheme={meta.color} variant="solid" fontSize="xs" px={2}>
            {category.count}
          </Badge>
        </HStack>
      </CardBody>
    </Card>
  );
};

const ExampleRow = ({ example, categoryTag, onViewCode, onUseTemplate, isEligible, eligibilityLoading, eligibilityReason, isLoggedIn }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [codeContent, setCodeContent] = useState(null);
  const [readmeContent, setReadmeContent] = useState(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingReadme, setLoadingReadme] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const toast = useToast();
  const meta = getCategoryMeta(categoryTag);

  const handleViewCode = async () => {
    if (codeContent !== null) {
      setIsExpanded(!isExpanded);
      return;
    }
    setLoadingCode(true);
    setCodeError(null);
    try {
      const data = await examplesApiService.getExampleFile(categoryTag, example.name, 'case');
      setCodeContent(typeof data === 'string' ? data : (data.content || JSON.stringify(data, null, 2)));
      setIsExpanded(true);
    } catch (err) {
      setCodeError('Failed to load case file');
    } finally {
      setLoadingCode(false);
    }
  };

  const handleViewReadme = async () => {
    if (readmeContent !== null) return;
    setLoadingReadme(true);
    try {
      const data = await examplesApiService.getExampleFile(categoryTag, example.name, 'readme');
      const raw = typeof data === 'string' ? data : (data.content || JSON.stringify(data, null, 2));
      setReadmeContent(prepareMarkdownMath(raw));
    } catch (err) {
      setReadmeContent('Failed to load README');
    } finally {
      setLoadingReadme(false);
    }
  };

  const handleCopy = () => {
    if (codeContent) {
      navigator.clipboard.writeText(codeContent);
      toast({ title: 'Copied to clipboard', status: 'success', duration: 2000, isClosable: true });
    }
  };

  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
    >
      <HStack
        px={5}
        py={4}
        justify="space-between"
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3} flex="1" minW="200px">
          <Icon as={meta.icon} color={`${meta.color}.500`} />
          <VStack align="start" spacing={1}>
            <Text fontWeight="semibold" fontSize="sm">{example.name}</Text>
            <Wrap spacing={1}>
              <WrapItem>
                <Badge colorScheme={getDimensionColor(example.dimension)} size="sm">
                  {example.dimension}
                </Badge>
              </WrapItem>
              {(example.files || []).map(f => (
                <WrapItem key={f}>
                  <Badge variant="outline" colorScheme="gray" size="sm">{f}</Badge>
                </WrapItem>
              ))}
              {(example.domains || []).map(d => (
                <WrapItem key={d}>
                  <Badge colorScheme="purple" variant="subtle" size="sm" fontSize="2xs">{d}</Badge>
                </WrapItem>
              ))}
            </Wrap>
            {example.keywords && example.keywords.length > 0 && (
              <Wrap spacing={1}>
                {example.keywords.map(kw => (
                  <WrapItem key={kw}>
                    <Badge colorScheme="gray" variant="outline" size="sm" fontSize="2xs" fontWeight="normal">{kw}</Badge>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </VStack>
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          {(example.has_description || example.description) && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FaFileAlt />}
              rightIcon={showDescription ? <FaChevronUp /> : <FaChevronDown />}
              onClick={() => setShowDescription(!showDescription)}
            >
              Description
            </Button>
          )}
          <Button
            as={RouterLink}
            to={getBusinessCaseUrl(example.name)}
            size="sm"
            variant="ghost"
            colorScheme="orange"
            leftIcon={<FaBriefcase />}
          >
            Business Case
          </Button>
          {example.has_readme && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<FaBookOpen />}
              onClick={handleViewReadme}
              isLoading={loadingReadme}
            >
              README
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            colorScheme="brand"
            leftIcon={loadingCode ? <Spinner size="xs" /> : <FaCode />}
            rightIcon={isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            onClick={handleViewCode}
            isLoading={loadingCode}
          >
            View Code
          </Button>
          <Tooltip
            label={
              !isLoggedIn
                ? 'Sign in to run examples'
                : !isEligible
                  ? eligibilityReason || 'Quota limit reached'
                  : 'Run this example as a simulation'
            }
            hasArrow
          >
            <Button
              size="sm"
              colorScheme="brand"
              leftIcon={<FaRocket />}
              isDisabled={!isLoggedIn || !isEligible}
              isLoading={eligibilityLoading}
              loadingText="Checking…"
              onClick={() => {
                if (codeContent) {
                  onUseTemplate(codeContent, example.name);
                } else {
                  examplesApiService.getExampleFile(categoryTag, example.name, 'case')
                    .then(data => {
                      const content = typeof data === 'string' ? data : (data.content || JSON.stringify(data, null, 2));
                      setCodeContent(content);
                      onUseTemplate(content, example.name);
                    })
                    .catch(() => {
                      toast({ title: 'Failed to load case file', status: 'error', duration: 3000 });
                    });
                }
              }}
            >
              Run Example
            </Button>
          </Tooltip>
        </HStack>
      </HStack>

      {codeError && (
        <Alert status="error" mx={5} mb={3} borderRadius="md">
          <AlertIcon />
          {codeError}
        </Alert>
      )}

      <Collapse in={showDescription && !!(example.has_description || example.description)} animateOpacity>
        <Box px={5} pb={3}>
          <Divider mb={3} />
          <Box
            px={8}
            py={5}
            bg="gray.50"
            borderRadius="md"
            border="1px"
            borderColor="gray.200"
            fontSize="sm"
            color="gray.700"
            lineHeight="tall"
            sx={{
              'h1': { fontSize: 'lg', fontWeight: 'bold', mb: 2, mt: 3, color: 'gray.800' },
              'h2': { fontSize: 'md', fontWeight: 'semibold', mb: 2, mt: 3, color: 'gray.700' },
              'h3': { fontSize: 'sm', fontWeight: 'semibold', mb: 1, mt: 2, color: 'gray.700' },
              'p': { mb: 2 },
              'ul, ol': { pl: 5, mb: 2 },
              'li': { mb: 1 },
              'code': { bg: 'gray.200', px: 1, borderRadius: 'sm', fontSize: 'xs', fontFamily: 'mono' },
              'pre': { bg: 'gray.800', color: 'green.300', p: 3, borderRadius: 'md', overflowX: 'auto', mb: 2, fontSize: 'xs' },
              '.katex-display': { overflowX: 'auto', py: 2 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {prepareMarkdownMath(example.description || '')}
            </ReactMarkdown>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={isExpanded} animateOpacity>
        <Box px={5} pb={4}>
          <Divider mb={3} />
          {readmeContent && (
            <Box mb={4} p={4} bg="blue.50" borderRadius="md" sx={{
              '& h1': { fontSize: 'lg', fontWeight: 'bold', color: 'blue.800', mt: 3, mb: 1 },
              '& h2': { fontSize: 'md', fontWeight: 'bold', color: 'blue.800', mt: 3, mb: 1 },
              '& h3': { fontSize: 'sm', fontWeight: 'bold', color: 'blue.700', mt: 2, mb: 1 },
              '& h4': { fontSize: 'sm', fontWeight: 'semibold', color: 'blue.700', mt: 2, mb: 1 },
              '& p': { fontSize: 'sm', color: 'gray.700', mb: 2, lineHeight: 'tall' },
              '& ul, & ol': { fontSize: 'sm', color: 'gray.700', pl: 5, mb: 2 },
              '& li': { mb: 1 },
              '& code': { fontSize: 'xs', bg: 'blue.100', px: 1, borderRadius: 'sm', fontFamily: 'mono' },
              '& pre': { fontSize: 'xs', bg: 'gray.800', color: 'green.300', p: 3, borderRadius: 'md', overflowX: 'auto', mb: 2 },
              '& pre code': { bg: 'transparent', px: 0 },
              '& table': { width: '100%', fontSize: 'sm', mb: 2 },
              '& th': { bg: 'blue.100', p: 2, textAlign: 'left', fontWeight: 'semibold', fontSize: 'xs' },
              '& td': { p: 2, borderBottom: '1px solid', borderColor: 'blue.100' },
              '& .katex': { fontSize: '0.95em' },
            }}>
              <Text fontWeight="semibold" mb={2} color="blue.700">README</Text>
              <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                {readmeContent}
              </ReactMarkdown>
            </Box>
          )}
          <Box borderRadius="lg" overflow="hidden" shadow="md">
            <HStack
              justify="space-between"
              bg="#282c34"
              px={4}
              py={2}
              borderBottom="1px solid"
              borderColor="whiteAlpha.100"
            >
              <HStack spacing={2}>
                <HStack spacing={1.5}>
                  <Box w={3} h={3} borderRadius="full" bg="red.400" />
                  <Box w={3} h={3} borderRadius="full" bg="yellow.400" />
                  <Box w={3} h={3} borderRadius="full" bg="green.400" />
                </HStack>
                <Text color="gray.500" fontFamily="mono" fontSize="xs" ml={2}>pyfr.ini</Text>
              </HStack>
              <Tooltip label="Copy to clipboard">
                <IconButton
                  icon={<FaCopy />}
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                  onClick={handleCopy}
                  aria-label="Copy code"
                  borderRadius="md"
                />
              </Tooltip>
            </HStack>
            <Box maxH="500px" overflowY="auto">
              <SyntaxHighlighter
                language="python"
                style={oneDark}
                showLineNumbers
                lineNumberStyle={{ color: '#636d83', fontSize: '0.7rem', minWidth: '2.5em', paddingRight: '1em', userSelect: 'none' }}
                customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.78rem', lineHeight: '1.6', background: '#1e1e2e' }}
                codeTagProps={{ style: { fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace" } }}
                wrapLongLines
              >
                {codeContent || ''}
              </SyntaxHighlighter>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const Examples = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { isOpen: isRunOpen, onOpen: onRunOpen, onClose: onRunClose } = useDisclosure();
  const [runForm, setRunForm] = useState({ jobName: '', jobDescription: '', code: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [jobEligibility, setJobEligibility] = useState(null);
  const [jobEligibilityLoading, setJobEligibilityLoading] = useState(true);


  useEffect(() => {
    fetchCategories();
    if (user) {
      fetchJobEligibility();
    } else {
      setJobEligibilityLoading(false);
    }
  }, [user]);

  const fetchJobEligibility = async () => {
    try {
      setJobEligibilityLoading(true);
      const eligibility = await apiService.checkJobEligibility();
      if (eligibility && typeof eligibility === 'object') {
        setJobEligibility({
          is_eligible: eligibility.is_eligible || false,
          remaining_hours: eligibility.remaining_hours || 0,
          reason: eligibility.reason || 'Unknown eligibility status'
        });
      } else {
        setJobEligibility({ is_eligible: false, remaining_hours: 0, reason: 'Invalid response format' });
      }
    } catch (err) {
      console.error('Failed to fetch job eligibility:', err);
      setJobEligibility({ is_eligible: false, remaining_hours: 0, reason: 'Failed to check eligibility' });
    } finally {
      setJobEligibilityLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await examplesApiService.getExamples();
      setCategories(data);
    } catch (err) {
      setError('Failed to load examples. Please try again.');
      console.error('Failed to fetch examples:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setSelectedCategory(null);
    setSearchQuery('');
  };

  const getFilteredResults = () => {
    if (!searchQuery.trim()) return { filteredCategories: categories, matchedExamples: [] };
    const q = searchQuery.toLowerCase();
    const matchedExamples = [];
    const filteredCategories = categories.filter(cat => {
      const categoryMatch =
        cat.label.toLowerCase().includes(q) ||
        cat.description.toLowerCase().includes(q) ||
        cat.tag.toLowerCase().includes(q) ||
        (cat.domains || []).some(d => d.toLowerCase().includes(q)) ||
        (cat.keywords || []).some(k => k.toLowerCase().includes(q));
      const matchingExamples = cat.examples.filter(ex =>
        ex.name.toLowerCase().includes(q) ||
        ex.dimension.toLowerCase().includes(q) ||
        (ex.files || []).some(f => f.toLowerCase().includes(q)) ||
        (ex.domains || []).some(d => d.toLowerCase().includes(q)) ||
        (ex.keywords || []).some(k => k.toLowerCase().includes(q))
      );
      if (matchingExamples.length > 0) {
        matchingExamples.forEach(ex => matchedExamples.push({ ...ex, categoryTag: cat.tag, categoryLabel: cat.label }));
      }
      return categoryMatch || matchingExamples.length > 0;
    });
    return { filteredCategories, matchedExamples };
  };

  const { filteredCategories, matchedExamples } = getFilteredResults();

  const handleRunExample = useCallback((code, exampleName) => {
    setRunForm({
      jobName: exampleName,
      jobDescription: `Running example: ${exampleName}`,
      code: code,
    });
    onRunOpen();
  }, [onRunOpen]);

  const handleSubmitRun = async () => {
    if (!runForm.jobName.trim()) {
      toast({ title: 'Please enter a job name', status: 'error', duration: 3000 });
      return;
    }
    if (!runForm.code.trim()) {
      toast({ title: 'No case file code found', status: 'error', duration: 3000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const eligibility = await apiService.checkJobEligibility();
      if (!eligibility || !eligibility.is_eligible) {
        toast({
          title: 'Not Eligible',
          description: eligibility?.reason || 'Please check your credits.',
          status: 'error',
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      const zip = new JSZip();
      zip.file('pyfr.ini', runForm.code);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFile = new File([zipBlob], `${runForm.jobName}.zip`, { type: 'application/zip' });

      const formData = new FormData();
      formData.append('tool_name', 'mfc');
      formData.append('zip_file', zipFile);
      formData.append('job_name', runForm.jobName);
      formData.append('job_desc', runForm.jobDescription);

      const response = await apiService.startSimulation(formData);

      toast({
        title: 'Simulation Started!',
        description: `Job "${runForm.jobName}" has been queued. Job ID: ${response.job_id}`,
        status: 'success',
        duration: 5000,
      });

      onRunClose();
      navigate('/dashboard');
    } catch (err) {
      console.error('Run example error:', err);
      toast({
        title: 'Failed to start simulation',
        description: err.message || 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardBg = useColorModeValue('white', 'gray.700');

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <Helmet>
        <title>CFD Simulation Examples & Templates | SRS</title>
        <meta name="description" content="Browse ready-to-run CFD simulation examples: turbulence benchmarks, shock dynamics, multiphase flows and more — launch any template on cloud GPUs in one click." />
        <link rel="canonical" href="https://srs.numericalai.net/examples" />
        <meta property="og:title" content="CFD Simulation Examples & Templates | SRS" />
        <meta property="og:description" content="Browse ready-to-run CFD simulation examples: turbulence benchmarks, shock dynamics, multiphase flows and more — launch any template on cloud GPUs in one click." />
        <meta property="og:url" content="https://srs.numericalai.net/examples" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CFD Simulation Examples & Templates | SRS" />
        <meta name="twitter:description" content="Browse ready-to-run CFD simulation examples: turbulence benchmarks, shock dynamics, multiphase flows and more — launch any template on cloud GPUs in one click." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl" py={4}>
          <HStack justify="space-between">
            <HStack
              spacing={1}
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }}
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: 'scale(0.98)' }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <HStack spacing={4}>
              <NavDropdowns />
              {user ? (
                <>
                  <Button
                    leftIcon={<FaCloud />}
                    colorScheme="brand"
                    size="sm"
                    variant="ghost"
                    as={RouterLink}
                    to="/dashboard"
                  >
                    Dashboard
                  </Button>
                  <Button
                    leftIcon={<FaSignOutAlt />}
                    variant="outline"
                    size="sm"
                    onClick={() => logout()}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" as={RouterLink} to="/signin">Sign In</Button>
                  <Button colorScheme="brand" as={RouterLink} to="/create-user">Get Started</Button>
                </>
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Hero */}
      <Box bg="linear-gradient(135deg, #0891b2 0%, #155e75 100%)" color="white" py={12}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaRocket} w={16} h={16} />
            <Heading as="h1" size="2xl">Launch Templates</Heading>
            <Text fontSize="xl" maxW="2xl" opacity={0.9}>
              Explore ready-to-run CFD templates. Learn from real case files and launch your own simulations.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Box flex="1" bg="gray.50">
        <Container maxW="container.xl" py={10}>
          {/* Breadcrumb */}
          <Breadcrumb
            separator={<FaChevronRight color="gray.400" />}
            mb={6}
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={handleBack}
                color={selectedCategory ? 'brand.500' : 'gray.600'}
                cursor="pointer"
                fontWeight="semibold"
              >
                All Categories
              </BreadcrumbLink>
            </BreadcrumbItem>
            {selectedCategory && (
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink color="gray.600">{selectedCategory.label}</BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </Breadcrumb>

          {/* Search */}
          {!loading && !error && !selectedCategory && (
            <Box mb={6}>
              <Flex
                gap={4}
                align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                flexWrap="wrap"
              >
                <InputGroup size="lg" flex="1" minW={{ base: '100%', md: '240px' }} maxW={{ base: '100%', lg: '600px' }}>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by name, dimension, category…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="white"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                  />
                  {searchQuery && (
                    <InputRightElement>
                      <CloseButton size="sm" onClick={() => setSearchQuery('')} />
                    </InputRightElement>
                  )}
                </InputGroup>
                <Link
                  as={RouterLink}
                  to="/executive-brief"
                  color="brand.500"
                  fontWeight="semibold"
                  fontSize="sm"
                  whiteSpace={{ base: 'normal', md: 'nowrap' }}
                  alignSelf={{ base: 'flex-start', md: 'center' }}
                  pt={{ base: 0, md: 2 }}
                >
                  Request an industry executive brief (PDF) →
                </Link>
              </Flex>
              {searchQuery && (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  {filteredCategories.length} {filteredCategories.length === 1 ? 'category' : 'categories'}
                  {matchedExamples.length > 0 && `, ${matchedExamples.length} direct ${matchedExamples.length === 1 ? 'match' : 'matches'}`}
                </Text>
              )}
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <VStack py={20} spacing={4}>
              <Spinner size="xl" color="brand.500" thickness="4px" />
              <Text color="gray.500">Loading examples…</Text>
            </VStack>
          )}

          {/* Error */}
          {error && !loading && (
            <Alert status="error" borderRadius="md" mb={6}>
              <AlertIcon />
              {error}
              <Button size="sm" ml={4} onClick={fetchCategories}>Retry</Button>
            </Alert>
          )}

          {/* Category Grid */}
          {!loading && !error && !selectedCategory && (
            <VStack align="stretch" spacing={6}>
              {filteredCategories.length === 0 && searchQuery ? (
                <VStack py={12} spacing={3}>
                  <Icon as={FaSearch} boxSize={10} color="gray.300" />
                  <Text color="gray.500" fontSize="lg">No results for "{searchQuery}"</Text>
                  <Button size="sm" variant="link" colorScheme="brand" onClick={() => setSearchQuery('')}>
                    Clear search
                  </Button>
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredCategories.map(cat => (
                    <CategoryCard
                      key={cat.tag}
                      category={cat}
                      onClick={() => handleSelectCategory(cat)}
                    />
                  ))}
                </SimpleGrid>
              )}

              {searchQuery && matchedExamples.length > 0 && (
                <Box>
                  <Heading size="sm" color="gray.600" mb={3}>Matching examples</Heading>
                  <VStack align="stretch" spacing={3}>
                    {matchedExamples.map(example => (
                      <Box key={`${example.categoryTag}-${example.name}`}>
                        <Badge colorScheme="gray" mb={1} fontSize="xs">{example.categoryLabel}</Badge>
                        <ExampleRow
                          example={example}
                          categoryTag={example.categoryTag}
                          onViewCode={() => {}}
                          onUseTemplate={handleRunExample}
                          isEligible={jobEligibility?.is_eligible}
                          eligibilityLoading={jobEligibilityLoading}
                          eligibilityReason={jobEligibility?.reason}
                          isLoggedIn={!!user}
                        />
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          )}

          {/* Category Detail */}
          {!loading && !error && selectedCategory && (() => {
            const selectedMeta = getCategoryMeta(selectedCategory.tag);
            return (
            <VStack align="stretch" spacing={6}>
              <HStack>
                <Button
                  leftIcon={<FaArrowLeft />}
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                >
                  Back to Categories
                </Button>
              </HStack>

              <Card bg={cardBg} shadow="sm">
                <CardBody>
                  <HStack spacing={4} mb={2}>
                    <Icon
                      as={selectedMeta.icon}
                      boxSize={6}
                      color={`${selectedMeta.color}.500`}
                    />
                    <Heading as="h2" size="lg">{selectedCategory.label}</Heading>
                    <Badge colorScheme="brand" fontSize="sm" px={2}>
                      {selectedCategory.count} {selectedCategory.count === 1 ? 'example' : 'examples'}
                    </Badge>
                  </HStack>
                  <Text color="gray.600" mb={3}>{selectedCategory.description}</Text>
                  {selectedCategory.domains && selectedCategory.domains.length > 0 && (
                    <Wrap spacing={1} mb={2}>
                      {selectedCategory.domains.map(domain => (
                        <WrapItem key={domain}>
                          <Badge colorScheme="gray" variant="outline" fontSize="2xs" fontWeight="medium">{domain}</Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                  {selectedCategory.keywords && selectedCategory.keywords.length > 0 && (
                    <Wrap spacing={1}>
                      {selectedCategory.keywords.map(kw => (
                        <WrapItem key={kw}>
                          <Badge
                            colorScheme={selectedMeta.color}
                            variant="subtle"
                            fontSize="2xs"
                          >
                            {kw}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </CardBody>
              </Card>

              <VStack align="stretch" spacing={3}>
                {selectedCategory.examples.map(example => (
                  <ExampleRow
                    key={example.name}
                    example={example}
                    categoryTag={selectedCategory.tag}
                    onViewCode={() => {}}
                    onUseTemplate={handleRunExample}
                    isEligible={jobEligibility?.is_eligible}
                    eligibilityLoading={jobEligibilityLoading}
                    eligibilityReason={jobEligibility?.reason}
                    isLoggedIn={!!user}
                  />
                ))}
              </VStack>
            </VStack>
            );
          })()}
        </Container>
      </Box>

      {/* Run Example Modal */}
      <Modal isOpen={isRunOpen} onClose={onRunClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent mx={4}>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaRocket} color="brand.500" />
              <Text>Run Example</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={4}>
            <VStack spacing={5} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="semibold">Job Name</FormLabel>
                <Input
                  value={runForm.jobName}
                  onChange={(e) => setRunForm(prev => ({ ...prev, jobName: e.target.value }))}
                  placeholder="Enter job name"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold">Description</FormLabel>
                <Textarea
                  value={runForm.jobDescription}
                  onChange={(e) => setRunForm(prev => ({ ...prev, jobDescription: e.target.value }))}
                  placeholder="Enter job description"
                  rows={2}
                />
              </FormControl>

              <FormControl isRequired>
                <HStack justify="space-between" mb={1}>
                  <FormLabel fontWeight="semibold" mb={0}>pyfr.ini</FormLabel>
                  <HStack spacing={1}>
                    <Icon as={FaEdit} color="gray.400" boxSize={3} />
                    <Text fontSize="xs" color="gray.400">Editable</Text>
                  </HStack>
                </HStack>
                <Box borderRadius="lg" overflow="hidden" shadow="md">
                  <HStack
                    justify="space-between"
                    bg="#282c34"
                    px={4}
                    py={2}
                    borderBottom="1px solid"
                    borderColor="whiteAlpha.100"
                  >
                    <HStack spacing={2}>
                      <HStack spacing={1.5}>
                        <Box w={3} h={3} borderRadius="full" bg="red.400" />
                        <Box w={3} h={3} borderRadius="full" bg="yellow.400" />
                        <Box w={3} h={3} borderRadius="full" bg="green.400" />
                      </HStack>
                      <Text color="gray.500" fontFamily="mono" fontSize="xs" ml={2}>pyfr.ini</Text>
                    </HStack>
                  </HStack>
                  <Box bg="#1e1e2e" maxH="500px" overflowY="auto">
                    <HStack spacing={0} align="stretch">
                      <Box
                        pt="8px" pb="8px" pl={3} pr={3}
                        bg="rgba(0,0,0,0.2)"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.100"
                        userSelect="none" textAlign="right"
                        minW="48px" position="sticky" left={0}
                      >
                        {runForm.code && runForm.code.split('\n').map((_, i) => (
                          <Text key={i} fontSize="0.78rem" color="#636d83" fontFamily="'JetBrains Mono', 'Fira Code', monospace" lineHeight="21px">{i + 1}</Text>
                        ))}
                      </Box>
                      <Textarea
                        value={runForm.code}
                        onChange={(e) => setRunForm(prev => ({ ...prev, code: e.target.value }))}
                        fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace"
                        fontSize="0.78rem"
                        bg="transparent" color="#abb2bf"
                        resize="none"
                        pt="8px" pb="8px" pl={4} pr={4} border="none" lineHeight="21px"
                        _focus={{ boxShadow: 'none' }}
                        overflow="hidden"
                        rows={runForm.code ? Math.max(runForm.code.split('\n').length, 15) : 15}
                      />
                    </HStack>
                  </Box>
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onRunClose}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                leftIcon={isSubmitting ? <Spinner size="xs" /> : <FaPlay />}
                onClick={handleSubmitRun}
                isLoading={isSubmitting}
                loadingText="Submitting…"
              >
                Run Simulation
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Footer />
    </Box>
  );
};

export default Examples;
